import { useEffect, useMemo, useState } from "react";
import { Plus, Search, List, CalendarDays, ArrowUp, ArrowDown } from "lucide-react";
import FullScreenModal from "@/components/FullScreenModal";
import { formatCurrency } from "@vaulzo/core/constants";
import {
  MONTHS_LONG,
  MONTHS_SHORT,
  categoryLabel,
  searchExpenses,
  sortExpenses,
  defaultSortDir,
  calendarMonth,
  shortAmount,
} from "@vaulzo/core/domain/expenses";
import { kicker, PrimaryButton } from "./ui";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dayLabel = (date) => {
  const d = new Date(`${date}T00:00:00`);
  return `${DOW[d.getDay()]} ${Number(date.slice(8))} ${MONTHS_SHORT[Number(date.slice(5, 7)) - 1]}`;
};

// Date · What · Category · Amount; the category column goes on phones.
const COLS = "grid-cols-[64px_minmax(0,1fr)_88px] sm:grid-cols-[72px_minmax(0,1fr)_150px_96px]";
const CELL_CAT = "hidden sm:flex";
// Calendar shading, level 0 (nothing) to 4 (the month's biggest day).
const LEVEL = [
  "bg-vaulzo-surface text-vaulzo-ink",
  "bg-vaulzo-row text-vaulzo-ink",
  "bg-vaulzo-level2 text-vaulzo-ink",
  "bg-vaulzo-level3 text-vaulzo-ink",
  "bg-vaulzo-accent text-vaulzo-on-fill",
];

/** Everything folded into "General spending" this month as a ledger: a
 *  searchable, sortable table, or the same entries on a calendar. Tapping a
 *  row opens the edit dialog (delete lives there); tapping an empty calendar
 *  day starts a new entry on that date. */
export default function GeneralModal({ open, onClose, general, year, month, onAdd, onEdit }) {
  const [view, setView] = useState("table"); // "table" | "calendar"
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [day, setDay] = useState(null); // selected date in the calendar view
  useEffect(() => {
    if (open) {
      setView("table");
      setQuery("");
      setSort({ key: "date", dir: "desc" });
      setDay(null);
    }
  }, [open]);

  const total = useMemo(() => general.reduce((s, e) => s + e.amount, 0), [general]);
  const shown = useMemo(() => sortExpenses(searchExpenses(general, query), sort.key, sort.dir), [general, query, sort]);
  const shownTotal = useMemo(() => shown.reduce((s, e) => s + e.amount, 0), [shown]);
  const cal = useMemo(() => calendarMonth(year, month, general), [year, month, general]);
  const dayCell = day ? cal.cells.find((c) => c.date === day) : null;

  if (!open) return null;

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: defaultSortDir(key) }));
  const sortMark = (key) =>
    sort.key === key ? (
      sort.dir === "asc" ? (
        <ArrowUp className="h-3 w-3 text-vaulzo-ink" strokeWidth={2.6} aria-hidden />
      ) : (
        <ArrowDown className="h-3 w-3 text-vaulzo-ink" strokeWidth={2.6} aria-hidden />
      )
    ) : null;
  const head = (key, label, extra = "") => (
    <button
      type="button"
      onClick={() => toggleSort(key)}
      aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      className={`flex items-center gap-1 text-left text-[11px] uppercase tracking-[0.08em] text-vaulzo-faint transition-colors hover:text-vaulzo-ink ${extra}`}
    >
      {label}
      {sortMark(key)}
    </button>
  );
  const viewButton = (value, label, Icon) => (
    <button
      type="button"
      onClick={() => setView(value)}
      aria-pressed={view === value}
      aria-label={label}
      className={`flex h-9 w-9 items-center justify-center rounded-[9px] transition-colors ${
        view === value ? "bg-vaulzo-surface text-vaulzo-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)]" : "text-vaulzo-faint hover:text-vaulzo-ink"
      }`}
    >
      <Icon className="h-4 w-4" strokeWidth={2} aria-hidden />
    </button>
  );
  const entryRow = (e, i, { withDate = true } = {}) => (
    <button
      key={e.id}
      type="button"
      onClick={() => onEdit(e)}
      className={`grid w-full ${COLS} items-center gap-3 px-[14px] py-0 text-left text-[14px] transition-colors hover:bg-vaulzo-accent-tint/60 ${
        i % 2 ? "bg-vaulzo-bg" : "bg-vaulzo-surface"
      } h-[38px]`}
    >
      <span className="num text-vaulzo-faint">{withDate ? `${Number(e.date.slice(8))} ${MONTHS_SHORT[month - 1]}` : ""}</span>
      <span className="truncate text-vaulzo-ink">
        {e.name}
        {e.note && <span className="ml-2 text-[12px] text-vaulzo-fainter">{e.note}</span>}
      </span>
      <span className={`${CELL_CAT} truncate text-[13px] text-vaulzo-faint`}>{categoryLabel(e.category)}</span>
      <span className="num font-semibold text-vaulzo-ink">{formatCurrency(e.amount)}</span>
    </button>
  );

  return (
    <FullScreenModal open={open} onClose={onClose}>
      <div className="w-full max-w-[640px]">
        <div className={kicker}>General spending</div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="num m-0 text-[34px] font-bold tracking-[-0.03em] text-vaulzo-ink">{formatCurrency(total)}</h2>
            <div className="mt-2 text-sm text-vaulzo-faint">
              {MONTHS_LONG[month - 1]} {year} · {general.length} {general.length === 1 ? "entry" : "entries"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-[2px] rounded-[11px] bg-vaulzo-row p-1">
              {viewButton("table", "Show as a table", List)}
              {viewButton("calendar", "Show on a calendar", CalendarDays)}
            </div>
            <PrimaryButton type="button" small onClick={() => onAdd(view === "calendar" && day ? { date: day } : {})}>
              <Plus className="mr-[6px] h-4 w-4" strokeWidth={2.2} />
              Add
            </PrimaryButton>
          </div>
        </div>

        {view === "table" ? (
          <>
            <label className="mt-6 flex h-10 items-center gap-[10px] rounded-[10px] bg-vaulzo-row px-[14px]">
              <Search className="h-[15px] w-[15px] flex-none text-vaulzo-faint" strokeWidth={2} aria-hidden />
              <span className="sr-only">Search this month</span>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search this month"
                className="w-full bg-transparent text-[14px] text-vaulzo-ink outline-none placeholder:text-vaulzo-fainter"
              />
            </label>
            <div className="mt-4 overflow-hidden rounded-xl border border-vaulzo-hairline">
              <div className={`grid ${COLS} items-center gap-3 border-b border-vaulzo-hairline bg-vaulzo-bg px-[14px] h-9`}>
                {head("date", "Date")}
                {head("name", "What")}
                {head("category", "Category", CELL_CAT)}
                {head("amount", "Amount")}
              </div>
              {shown.length === 0 && (
                <div className="py-10 text-center text-sm text-vaulzo-subtle">{query ? `Nothing matches “${query}”.` : "Nothing here."}</div>
              )}
              {shown.map((e, i) => entryRow(e, i))}
              <div className={`grid ${COLS} items-center gap-3 border-t-2 border-vaulzo-ink px-[14px] h-10 text-[14px] font-semibold text-vaulzo-ink`}>
                <span />
                <span>
                  {shown.length} {shown.length === 1 ? "entry" : "entries"}
                  {query && shown.length !== general.length ? ` of ${general.length}` : ""}
                </span>
                <span className={CELL_CAT} />
                <span className="num">{formatCurrency(shownTotal)}</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-7 gap-1">
              {cal.weekdays.map((w) => (
                <div key={w} className="pb-[6px] text-center text-[11px] uppercase tracking-[0.08em] text-vaulzo-fainter">
                  {w}
                </div>
              ))}
              {cal.cells.map((c) =>
                c.blank ? (
                  <div key={c.key} />
                ) : (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => (c.rows.length ? setDay(day === c.date ? null : c.date) : onAdd({ date: c.date }))}
                    aria-label={c.rows.length ? `${dayLabel(c.date)}, ${formatCurrency(c.total)}` : `${dayLabel(c.date)}, nothing spent. Add an expense`}
                    aria-pressed={day === c.date}
                    className={`flex h-14 flex-col justify-between rounded-[10px] p-2 text-left transition-shadow sm:h-16 sm:p-[9px] ${LEVEL[c.level]} ${
                      day === c.date ? "ring-2 ring-inset ring-vaulzo-ink" : "hover:ring-1 hover:ring-inset hover:ring-vaulzo-subtle"
                    }`}
                  >
                    <span className="num text-[12px] opacity-75">{c.day}</span>
                    <span className="num text-[12px] font-semibold sm:text-[13px]">{c.total ? shortAmount(c.total) : ""}</span>
                  </button>
                )
              )}
            </div>
            {dayCell ? (
              <div className="mt-6 overflow-hidden rounded-xl border border-vaulzo-hairline">
                <div className="flex items-baseline justify-between border-b border-vaulzo-hairline bg-vaulzo-bg px-[14px] py-[10px]">
                  <span className="text-[14px] font-semibold text-vaulzo-ink">{dayLabel(dayCell.date)}</span>
                  <span className="num text-[14px] text-vaulzo-faint">{formatCurrency(dayCell.total)}</span>
                </div>
                {dayCell.rows.map((e, i) => entryRow(e, i, { withDate: false }))}
              </div>
            ) : (
              <div className="mt-6 text-center text-[13px] text-vaulzo-subtle">Darker days spent more. Tap a day for its entries, or an empty day to add one there.</div>
            )}
          </>
        )}
      </div>
    </FullScreenModal>
  );
}
